import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { NotaFiscalGlobalListItem, NotaFiscalStatus } from './types';
import { notaFiscalTemPdf } from '../notas-fiscais/types';

export interface NotaFiscalColaboradorMostrarDialogProps {
  open: boolean;
  nota: NotaFiscalGlobalListItem | null;
  onClose: () => void;
}

function formatBRL(value: number | null | undefined): string {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function temPdf(nota: NotaFiscalGlobalListItem): boolean {
  return notaFiscalTemPdf(nota);
}

function arquivoUrl(nota: NotaFiscalGlobalListItem): string {
  return `/api/v1/employees/${nota.employeeId}/notas-fiscais/${nota.id}/file`;
}

function statusChipColor(
  status: NotaFiscalStatus,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'APR':
    case 'CON':
      return 'success';
    case 'REP':
      return 'error';
    case 'SOL':
    case 'PAP':
      return 'warning';
    case 'NSO':
    case 'CAN':
      return 'default';
    default:
      return 'info';
  }
}

/**
 * Consulta da nota do colaborador. A guia do PDF mostra o arquivo na tela;
 * o download é opcional.
 */
export function NotaFiscalColaboradorMostrarDialog({
  open,
  nota,
  onClose,
}: NotaFiscalColaboradorMostrarDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [aba, setAba] = useState<'nota' | 'pdf'>('nota');
  const base = 'features.colaboradores.notasFiscaisGlobal.mostrar';

  function fechar(): void {
    setAba('nota');
    onClose();
  }

  const pdfSrc = nota && temPdf(nota) ? arquivoUrl(nota) : null;
  const cabecalho: Array<[string, string]> = nota
    ? [
        [t(`${base}.colaborador`), nota.colaboradorNome || '—'],
        [t(`${base}.cliente`), nota.clienteNome || '—'],
        [t(`${base}.competencia`), nota.periodo || '—'],
      ]
    : [];
  const espelho: Array<[string, string]> = nota
    ? [
        [t(`${base}.espelhoHoras`), nota.espelhoTotalHoras || '—'],
        [t(`${base}.espelhoValor`), formatBRL(nota.espelhoValor)],
      ]
    : [];
  const apontamento: Array<[string, string]> = nota
    ? [
        [t(`${base}.horasApontadas`), nota.apontamentoTotalHorasApontadas || '—'],
        [t(`${base}.horasAjustadas`), nota.apontamentoTotalHorasAjustadas || '—'],
        [t(`${base}.valorApontamento`), formatBRL(nota.apontamentoValor)],
        [t(`${base}.vale`), formatBRL(nota.valeValor)],
        [t(`${base}.valorReceber`), formatBRL(nota.valorReceberFormula)],
        [t(`${base}.valorRecebido`), formatBRL(nota.valorRecebido)],
      ]
    : [];

  return (
    <Dialog open={open} onClose={fechar} maxWidth="lg" fullWidth>
      <DialogTitle>{t(`${base}.title`)}</DialogTitle>
      <Tabs value={aba} onChange={(_, value: 'nota' | 'pdf') => setAba(value)} sx={{ px: 3 }}>
        <Tab value="nota" label={t(`${base}.abaNota`)} />
        <Tab value="pdf" label={t(`${base}.abaPdf`)} />
      </Tabs>
      <DialogContent dividers sx={{ minHeight: 420 }}>
        {aba === 'nota' && nota && (
          <Stack spacing={1.5}>
            <Chip
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              label={t(`features.colaboradores.notasFiscais.status.${nota.status}`)}
              color={statusChipColor(nota.status)}
              variant={nota.status === 'APR' || nota.status === 'CON' ? 'filled' : 'outlined'}
            />
            <Divider />
            {cabecalho.map(([rotulo, valor]) => (
              <Stack key={rotulo} direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>
                  {rotulo}
                </Typography>
                <Typography variant="body2">{valor}</Typography>
              </Stack>
            ))}
            <Typography variant="overline" color="text.secondary">
              {t(`${base}.espelho`)}
            </Typography>
            {espelho.map(([rotulo, valor]) => (
              <Stack key={rotulo} direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>
                  {rotulo}
                </Typography>
                <Typography variant="body2">{valor}</Typography>
              </Stack>
            ))}
            <Typography variant="overline" color="text.secondary">
              {t(`${base}.apontamento`)}
            </Typography>
            {apontamento.map(([rotulo, valor]) => (
              <Stack key={rotulo} direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>
                  {rotulo}
                </Typography>
                <Typography variant="body2">{valor}</Typography>
              </Stack>
            ))}
            {nota.motivoReprovacao && (
              <Typography variant="body2" color="error">
                {nota.motivoReprovacao}
              </Typography>
            )}
          </Stack>
        )}
        {aba === 'pdf' && nota && !temPdf(nota) && (
          <Typography variant="body2" color="text.secondary">
            {t(`${base}.semPdf`)}
          </Typography>
        )}
        {aba === 'pdf' && pdfSrc && (
          <Box
            component="iframe"
            src={pdfSrc}
            title={t(`${base}.abaPdf`)}
            sx={{ width: '100%', height: '70vh', border: 0 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {aba === 'pdf' && pdfSrc && nota && (
          <Button component="a" href={pdfSrc} download={nota.pdfNome || `nf-${nota.id}.pdf`}>
            {t(`${base}.download`)}
          </Button>
        )}
        <Button onClick={fechar}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
